import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { SITE } from "../data/siteData";
import Support from "./Support";

const API = process.env.REACT_APP_API_URL || "http://localhost:3001";

export default function PaymentModal({ onClose, currentUser }) {
  const navigate = useNavigate();
  const [step, setStep] = useState("form");
  const [mpesaPhone, setMpesa] = useState("");
  const [altPhone, setAlt] = useState("");
  const [refCode, setRefCode] = useState("");
  const [refResult, setRefResult] = useState(null);
  const [refBusy, setRefBusy] = useState(false);
  const [price, setPrice] = useState(SITE.priceFullRaw);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const pd = "Ksh " + price.toLocaleString();

  const checkRef = async () => {
    if (!refCode.trim()) return;
    setRefBusy(true);
    setRefResult(null);
    try {
      const r = await fetch(API + "/api/validate-referral", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: refCode.trim().toUpperCase() }),
      });
      const d = await r.json();
      if (d.valid) {
        setRefResult({ ok: true, name: d.referrerName });
        setPrice(SITE.priceReferredRaw);
      } else {
        setRefResult({ ok: false, error: d.error });
        setPrice(SITE.priceFullRaw);
      }
    } catch {
      setRefResult({ ok: false, error: "Could not verify. Please try again." });
    }
    setRefBusy(false);
  };

  const proceed = async () => {
    setError("");
    const ph = mpesaPhone.trim().replace(/\s+/g, "");
    if (!ph || ph.length < 9) {
      setError("Please enter the M-Pesa number you will use to pay.");
      return;
    }
    setLoading(true);
    // Save the phone number immediately to the database
    // This creates a record before payment so admin can match by phone number
    try {
      await fetch(API + "/api/payment/intent", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: currentUser.email,
          phone: ph,
          personalPhone: altPhone.trim() || null,
          referralCodeUsed: refResult?.ok ? refCode.trim().toUpperCase() : null,
          amount: price,
        }),
      });
    } catch {
      // Non-critical — show instructions even if save fails
    }
    setLoading(false);
    setStep("instructions");
  };

  // ── Instructions ────────────────────────────────────────────────────────

  if (step === "instructions")
    return (
      <Overlay>
        <Box>
          <Hdr title="Pay via M-Pesa" onClose={onClose} />
          <div style={{ padding: "16px 22px" }}>
            <div
              style={{
                background: "#e8f8fc",
                border: "1px solid #009dc4",
                borderRadius: 4,
                padding: "12px 14px",
                marginBottom: 18,
              }}
            >
              <KV
                k="Amount to pay"
                v={
                  <strong style={{ color: "#009dc4", fontSize: 17 }}>
                    {pd}
                  </strong>
                }
              />
              <KV
                k="Till Number"
                v={
                  <strong
                    style={{
                      fontFamily: "monospace",
                      fontSize: 17,
                      letterSpacing: 1,
                    }}
                  >
                    {SITE.till}
                  </strong>
                }
              />
              <KV k="Paying from" v={mpesaPhone} />
              {altPhone.trim() && <KV k="Alt contact" v={altPhone} />}
              {refResult?.ok && (
                <KV
                  k="Referral discount"
                  v={
                    <span style={{ color: "#16a34a", fontWeight: 700 }}>
                      Applied
                    </span>
                  }
                />
              )}
            </div>

            {[
              "Open M-Pesa on your phone",
              "Select Lipa na M-Pesa then Buy Goods and Services",
              "Enter Till Number: " + SITE.till,
              "Enter amount: " + pd,
              "Enter your M-Pesa PIN and confirm",
              "Save your M-Pesa confirmation SMS as proof of payment",
              "Once we confirm your payment we will send your access code and referral code to you via SMS on " +
                mpesaPhone +
                (altPhone.trim() ? " and " + altPhone.trim() : ""),
            ].map((txt, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  gap: 10,
                  marginBottom: 10,
                  alignItems: "flex-start",
                }}
              >
                <div
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    background: "#009dc4",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 11,
                    fontWeight: 800,
                    flexShrink: 0,
                  }}
                >
                  {i + 1}
                </div>
                <p
                  style={{
                    fontSize: 13,
                    color: "#282a35",
                    lineHeight: 1.6,
                    margin: 0,
                  }}
                >
                  {txt}
                </p>
              </div>
            ))}

            <div
              style={{
                background: "#fffbeb",
                border: "1px solid #fde68a",
                borderRadius: 4,
                padding: "10px 12px",
                marginTop: 10,
              }}
            >
              <p
                style={{
                  fontSize: 12,
                  color: "#78350f",
                  margin: 0,
                  lineHeight: 1.7,
                }}
              >
                <strong>Note:</strong> Your access code is tied to your account
                at <strong>{currentUser?.email}</strong> and can only be used on
                that account. Enter it at{" "}
                <strong>skilllaunch.co.ke/unlock</strong> once you receive it.
              </p>
            </div>
          </div>

          <div style={{ padding: "0 22px 18px" }}>
            <p
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: "#282a35",
                margin: "0 0 6px",
              }}
            >
              Already received your code?
            </p>
            <button
              onClick={() => {
                onClose();
                navigate("/unlock");
              }}
              style={{
                ...PB,
                width: "100%",
                padding: "10px 0",
                fontSize: 14,
                marginBottom: 8,
              }}
            >
              Enter my access code
            </button>
            <button
              onClick={onClose}
              style={{
                width: "100%",
                padding: "9px 0",
                background: "none",
                border: "1px solid #ddd",
                borderRadius: 4,
                color: "#666",
                fontSize: 13,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              Close — I will enter the code later
            </button>
            <Support style={{ marginTop: 12 }} />
          </div>
        </Box>
      </Overlay>
    );

  // ── Form ─────────────────────────────────────────────────────────────────

  return (
    <Overlay>
      <Box>
        <Hdr title="Get full access" onClose={onClose} />
        <div style={{ padding: "16px 22px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
              padding: "12px 14px",
              background: "#f5f5f5",
              borderRadius: 4,
              border: "1px solid #e0e0e0",
              marginBottom: 18,
            }}
          >
            <div>
              <p
                style={{
                  fontSize: 11,
                  color: "#777",
                  margin: "0 0 2px",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  fontWeight: 700,
                }}
              >
                One-time payment
              </p>
              <p
                style={{
                  fontSize: 24,
                  fontWeight: 900,
                  color: "#009dc4",
                  margin: 0,
                }}
              >
                {pd}
              </p>
              {refResult?.ok && (
                <p
                  style={{
                    fontSize: 11,
                    color: "#16a34a",
                    margin: "3px 0 0",
                    fontWeight: 700,
                  }}
                >
                  Referral discount applied — saved Ksh{" "}
                  {(SITE.priceFullRaw - SITE.priceReferredRaw).toLocaleString()}
                </p>
              )}
            </div>
            <div style={{ textAlign: "right" }}>
              <p style={{ fontSize: 11, color: "#aaa", margin: 0 }}>
                Via M-Pesa
              </p>
              <p style={{ fontSize: 11, color: "#aaa", margin: 0 }}>
                No card needed
              </p>
              <p style={{ fontSize: 11, color: "#aaa", margin: 0 }}>
                No subscription
              </p>
            </div>
          </div>

          {/* Referral code */}
          <div style={{ marginBottom: 16 }}>
            <label style={LB}>
              Referral code
              <span
                style={{
                  color: "#aaa",
                  fontWeight: 400,
                  fontSize: 12,
                  marginLeft: 6,
                }}
              >
                optional — reduces price to Ksh{" "}
                {SITE.priceReferredRaw.toLocaleString()}
              </span>
            </label>
            <div style={{ display: "flex", gap: 7 }}>
              <input
                value={refCode}
                placeholder="e.g. REFAB12C"
                onChange={(e) => {
                  setRefCode(e.target.value.toUpperCase());
                  setRefResult(null);
                  if (!e.target.value.trim()) setPrice(SITE.priceFullRaw);
                }}
                style={{
                  ...FI,
                  flex: 1,
                  fontFamily: "monospace",
                  letterSpacing: 1,
                }}
              />
              <button
                onClick={checkRef}
                disabled={!refCode.trim() || refBusy}
                style={{
                  ...PB,
                  opacity: !refCode.trim() ? 0.5 : 1,
                  cursor: !refCode.trim() ? "not-allowed" : "pointer",
                }}
              >
                {refBusy ? "..." : "Apply"}
              </button>
            </div>
            {refResult?.ok && (
              <p
                style={{
                  fontSize: 12,
                  color: "#16a34a",
                  margin: "4px 0 0",
                  fontWeight: 600,
                }}
              >
                Valid — referred by {refResult.name}
              </p>
            )}
            {refResult?.ok === false && (
              <p style={{ fontSize: 12, color: "#dc2626", margin: "4px 0 0" }}>
                {refResult.error}
              </p>
            )}
          </div>

          {/* M-Pesa number — required, saved immediately */}
          <div style={{ marginBottom: 14 }}>
            <label style={LB}>
              M-Pesa number you will pay from{" "}
              <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <input
              value={mpesaPhone}
              placeholder="e.g. 0712345678"
              type="tel"
              onChange={(e) => {
                setMpesa(e.target.value);
                setError("");
              }}
              style={FI}
            />
            <p style={{ fontSize: 11, color: "#999", marginTop: 4 }}>
              We record this number now so we can match your payment to your
              account.
            </p>
          </div>

          {/* Alt contact — optional */}
          <div style={{ marginBottom: 16 }}>
            <label style={LB}>
              Alternative contact number
              <span
                style={{
                  color: "#aaa",
                  fontWeight: 400,
                  fontSize: 12,
                  marginLeft: 6,
                }}
              >
                optional
              </span>
            </label>
            <input
              value={altPhone}
              placeholder="e.g. 0712345678"
              type="tel"
              onChange={(e) => setAlt(e.target.value)}
              style={FI}
            />
            <p style={{ fontSize: 11, color: "#999", marginTop: 4 }}>
              We will send your codes to both numbers if provided.
            </p>
          </div>

          {error && (
            <p style={{ fontSize: 13, color: "#dc2626", margin: "0 0 12px" }}>
              {error}
            </p>
          )}
        </div>

        <div style={{ padding: "0 22px 18px" }}>
          <button
            onClick={proceed}
            disabled={loading}
            style={{
              ...PB,
              width: "100%",
              padding: "11px 0",
              fontSize: 14,
              background: loading ? "#aaa" : "#009dc4",
              cursor: loading ? "not-allowed" : "pointer",
            }}
          >
            {loading ? "Saving..." : "Proceed to payment instructions"}
          </button>
          <Support style={{ marginTop: 12 }} />
        </div>
      </Box>
    </Overlay>
  );
}

function Overlay({ children }) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      {children}
    </div>
  );
}
function Box({ children }) {
  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 4,
        maxWidth: 440,
        width: "100%",
        maxHeight: "92vh",
        overflowY: "auto",
        boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
      }}
    >
      {children}
    </div>
  );
}
function Hdr({ title, onClose }) {
  return (
    <div
      style={{
        padding: "14px 22px",
        borderBottom: "1px solid #e5e5e5",
        borderTop: "3px solid #009dc4",
        borderRadius: "4px 4px 0 0",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <h3
        style={{ fontSize: 16, fontWeight: 700, color: "#282a35", margin: 0 }}
      >
        {title}
      </h3>
      <button
        onClick={onClose}
        style={{
          background: "none",
          border: "none",
          fontSize: 20,
          color: "#aaa",
          cursor: "pointer",
          lineHeight: 1,
          padding: 0,
        }}
      >
        x
      </button>
    </div>
  );
}
function KV({ k, v }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 6,
      }}
    >
      <span style={{ fontSize: 12, color: "#666" }}>{k}</span>
      <span style={{ fontSize: 13, color: "#282a35" }}>{v}</span>
    </div>
  );
}

const LB = {
  display: "block",
  fontSize: 13,
  fontWeight: 600,
  color: "#282a35",
  marginBottom: 5,
};
const FI = {
  width: "100%",
  padding: "8px 11px",
  border: "1px solid #ccc",
  borderRadius: 4,
  fontSize: 14,
  color: "#282a35",
  background: "#fff",
  boxSizing: "border-box",
  fontFamily: "inherit",
};
const PB = {
  padding: "8px 16px",
  background: "#009dc4",
  border: "none",
  borderRadius: 4,
  color: "#fff",
  fontSize: 13,
  fontWeight: 700,
  cursor: "pointer",
  fontFamily: "inherit",
};
